import { useState, useEffect } from "react";
import { Search, Plus, Edit2, Trash2, Loader2, X, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Users } from "lucide-react";
import { adminService, Client, Unidade, ClientUser } from "../../../services/adminService";

export function ClientManagement() {
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showUnidadeModal, setShowUnidadeModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [editingUnidade, setEditingUnidade] = useState<Unidade | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [expandedClient, setExpandedClient] = useState<string | null>(null);
  const [expandedTab, setExpandedTab] = useState<"unidades" | "usuarios">("unidades");
  const [clientUnidades, setClientUnidades] = useState<Unidade[]>([]);
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<ClientUser | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [formData, setFormData] = useState({
    razaoSocial: "", nomeFantasia: "", cnpj: "", email: "", password: "",
    contatoNome: "", contatoTelefone: "", contatoEmail: "",
    cep: "", logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", estado: ""
  });

  const [unidadeForm, setUnidadeForm] = useState({
    nome: "", cnpj: "",
    contatoNome: "", contatoTelefone: "", contatoEmail: "",
    cep: "", logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", estado: ""
  });

  const [userForm, setUserForm] = useState({ 
    name: "", email: "", password: "", role: "visualizador",
    permPmoc: true, permNf: true, permProposta: true 
  });

  useEffect(() => { loadClients(); }, [searchTerm]);

  const loadClients = async () => {
    setIsLoading(true);
    try {
      const result = await adminService.listClients(searchTerm);
      setClients(result.data);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUnidades = async (clientId: string) => {
    try {
      const unidades = await adminService.listUnidades(clientId);
      setClientUnidades(unidades);
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
    }
  };

  const loadClientUsers = async (clientId: string) => {
    try {
      const users = await adminService.listClientUsers(clientId);
      setClientUsers(users);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    }
  };

  const toggleExpand = async (clientId: string) => {
    if (expandedClient === clientId) {
      setExpandedClient(null);
    } else {
      setExpandedClient(clientId);
      setExpandedTab("unidades");
      await loadUnidades(clientId);
      await loadClientUsers(clientId);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await adminService.updateClient(editingClient.id, formData);
        setMessage({ type: "success", text: "Cliente atualizado com sucesso!" });
      } else {
        await adminService.createClient(formData as any);
        setMessage({ type: "success", text: "Cliente criado com sucesso!" });
      }
      closeModal();
      loadClients();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao salvar cliente" });
    }
  };

  const handleUnidadeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId) return;
    try {
      if (editingUnidade) {
        await adminService.updateUnidade(editingUnidade.id, unidadeForm);
        setMessage({ type: "success", text: "Unidade atualizada com sucesso!" });
      } else {
        await adminService.createUnidade(selectedClientId, unidadeForm);
        setMessage({ type: "success", text: "Unidade criada com sucesso!" });
      }
      closeUnidadeModal();
      loadUnidades(selectedClientId);
      loadClients();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao salvar unidade" });
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      razaoSocial: client.razaoSocial, nomeFantasia: client.nomeFantasia || "",
      cnpj: client.cnpj, email: client.email, password: "",
      contatoNome: client.contatoNome || "", contatoTelefone: client.contatoTelefone || "",
      contatoEmail: client.contatoEmail || "", cep: client.cep || "",
      logradouro: client.logradouro || "", numero: client.numero || "",
      complemento: client.complemento || "", bairro: client.bairro || "",
      cidade: client.cidade || "", estado: client.estado || ""
    });
    setShowModal(true);
  };

  const handleEditUnidade = (unidade: Unidade, clientId: string) => {
    setEditingUnidade(unidade);
    setSelectedClientId(clientId);
    setUnidadeForm({
      nome: unidade.nome, cnpj: unidade.cnpj || "",
      contatoNome: unidade.contatoNome || "", contatoTelefone: unidade.contatoTelefone || "",
      contatoEmail: unidade.contatoEmail || "", cep: unidade.cep || "",
      logradouro: unidade.logradouro || "", numero: unidade.numero || "",
      complemento: unidade.complemento || "", bairro: unidade.bairro || "",
      cidade: unidade.cidade || "", estado: unidade.estado || ""
    });
    setShowUnidadeModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Deseja desativar este cliente?")) return;
    try {
      await adminService.deleteClient(id);
      setMessage({ type: "success", text: "Cliente desativado!" });
      loadClients();
    } catch { setMessage({ type: "error", text: "Erro ao desativar cliente" }); }
  };

  const handleDeleteUnidade = async (id: number) => {
    if (!confirm("Deseja desativar esta unidade?")) return;
    try {
      await adminService.deleteUnidade(id);
      setMessage({ type: "success", text: "Unidade desativada!" });
      if (expandedClient) loadUnidades(expandedClient);
      loadClients();
    } catch { setMessage({ type: "error", text: "Erro ao desativar unidade" }); }
  };

  const openNewModal = () => {
    setEditingClient(null);
    setFormData({ razaoSocial: "", nomeFantasia: "", cnpj: "", email: "", password: "",
      contatoNome: "", contatoTelefone: "", contatoEmail: "", cep: "", logradouro: "",
      numero: "", complemento: "", bairro: "", cidade: "", estado: "" });
    setShowModal(true);
  };

  const openNewUnidadeModal = (clientId: string) => {
    setEditingUnidade(null);
    setSelectedClientId(clientId);
    setUnidadeForm({ nome: "", cnpj: "", contatoNome: "", contatoTelefone: "", contatoEmail: "",
      cep: "", logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "" });
    setShowUnidadeModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingClient(null); };
  const closeUnidadeModal = () => { setShowUnidadeModal(false); setEditingUnidade(null); };
  const closeUserModal = () => { setShowUserModal(false); setEditingUser(null); };

  const openNewUserModal = (clientId: string) => {
    setEditingUser(null);
    setSelectedClientId(clientId);
    setUserForm({ name: "", email: "", password: "", role: "visualizador", permPmoc: true, permNf: true, permProposta: true });
    setShowUserModal(true);
  };

  const handleEditUser = (user: ClientUser, clientId: string) => {
    setEditingUser(user);
    setSelectedClientId(clientId);
    // Parse permissões do JSON
    const perms = user.tiposPermitidos ? JSON.parse(user.tiposPermitidos) : ["pmoc", "nf", "proposta"];
    setUserForm({ 
      name: user.name, 
      email: user.email, 
      password: "", 
      role: user.role,
      permPmoc: perms.includes("pmoc"),
      permNf: perms.includes("nf"),
      permProposta: perms.includes("proposta"),
    });
    setShowUserModal(true);
  };

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId) return;
    try {
      // Montar array de permissões
      const tiposPermitidos: string[] = [];
      if (userForm.permPmoc) tiposPermitidos.push("pmoc");
      if (userForm.permNf) tiposPermitidos.push("nf");
      if (userForm.permProposta) tiposPermitidos.push("proposta");
      
      const dataToSend = {
        name: userForm.name,
        email: userForm.email,
        password: userForm.password,
        role: userForm.role,
        tiposPermitidos: JSON.stringify(tiposPermitidos),
      };
      
      if (editingUser) {
        await adminService.updateClientUser(editingUser.id, dataToSend);
        setMessage({ type: "success", text: "Usuário atualizado com sucesso!" });
      } else {
        await adminService.createClientUser(selectedClientId, dataToSend as any);
        setMessage({ type: "success", text: "Usuário criado com sucesso!" });
      }
      closeUserModal();
      loadClientUsers(selectedClientId);
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao salvar usuário" });
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (!confirm("Deseja desativar este usuário?")) return;
    try {
      await adminService.deleteClientUser(id);
      setMessage({ type: "success", text: "Usuário desativado!" });
      if (expandedClient) loadClientUsers(expandedClient);
    } catch { setMessage({ type: "error", text: "Erro ao desativar usuário" }); }
  };

  const clientRoles = [
    { value: "admin_cliente", label: "Administrador" },
    { value: "gerente", label: "Gerente" },
    { value: "visualizador", label: "Visualizador" },
  ];


  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
          {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Buscar clientes..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-400" />
        </div>
        <button onClick={openNewModal} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <Plus className="w-4 h-4" /> Novo Cliente
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12 text-gray-500">Nenhum cliente encontrado</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {clients.map((client) => (
              <div key={client.id}>
                <div className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{client.razaoSocial}</p>
                    <p className="text-sm text-gray-500">{client.email} • {client.cnpj}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500">{client._count?.unidades || 0} unidades</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${client.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                      {client.active ? "Ativo" : "Inativo"}
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => toggleExpand(client.id)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                        {expandedClient === client.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleEdit(client)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(client.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
                {expandedClient === client.id && (
                  <div className="bg-gray-50 p-4 border-t">
                    {/* Abas */}
                    <div className="flex gap-4 mb-4 border-b">
                      <button onClick={() => setExpandedTab("unidades")} 
                        className={`pb-2 px-1 text-sm font-medium border-b-2 ${expandedTab === "unidades" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                        Unidades ({clientUnidades.length})
                      </button>
                      <button onClick={() => setExpandedTab("usuarios")} 
                        className={`pb-2 px-1 text-sm font-medium border-b-2 flex items-center gap-1 ${expandedTab === "usuarios" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                        <Users className="w-4 h-4" /> Usuários ({clientUsers.length})
                      </button>
                    </div>

                    {/* Conteúdo Unidades */}
                    {expandedTab === "unidades" && (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-700">Unidades</h4>
                          <button onClick={() => openNewUnidadeModal(client.id)} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                            <Plus className="w-4 h-4" /> Nova Unidade
                          </button>
                        </div>
                        {clientUnidades.length === 0 ? (
                          <p className="text-sm text-gray-500">Nenhuma unidade cadastrada</p>
                        ) : (
                          <div className="space-y-2">
                            {clientUnidades.map((unidade) => (
                              <div key={unidade.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                                <div>
                                  <p className="font-medium text-gray-900">{unidade.nome}</p>
                                  <p className="text-sm text-gray-500">{unidade.cnpj || "Sem CNPJ"} • {unidade.cidade || "Sem cidade"}/{unidade.estado || ""}</p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button onClick={() => handleEditUnidade(unidade, client.id)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                                  <button onClick={() => handleDeleteUnidade(unidade.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {/* Conteúdo Usuários */}
                    {expandedTab === "usuarios" && (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-700">Usuários de Acesso</h4>
                          <button onClick={() => openNewUserModal(client.id)} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                            <Plus className="w-4 h-4" /> Novo Usuário
                          </button>
                        </div>
                        {clientUsers.length === 0 ? (
                          <p className="text-sm text-gray-500">Nenhum usuário adicional cadastrado. O cliente pode acessar com o email principal.</p>
                        ) : (
                          <div className="space-y-2">
                            {clientUsers.map((user) => (
                              <div key={user.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                                <div>
                                  <p className="font-medium text-gray-900">{user.name}</p>
                                  <p className="text-sm text-gray-500">{user.email} • {clientRoles.find(r => r.value === user.role)?.label || user.role}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 rounded-full text-xs ${user.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                    {user.active ? "Ativo" : "Inativo"}
                                  </span>
                                  <button onClick={() => handleEditUser(user, client.id)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                                  <button onClick={() => handleDeleteUser(user.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>


      {/* Modal Cliente */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-2xl p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingClient ? "Editar Cliente" : "Novo Cliente"}</h3>
              <button onClick={closeModal}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Razão Social *</label>
                  <input type="text" value={formData.razaoSocial} onChange={(e) => setFormData(p => ({ ...p, razaoSocial: e.target.value }))} required className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome Fantasia</label>
                  <input type="text" value={formData.nomeFantasia} onChange={(e) => setFormData(p => ({ ...p, nomeFantasia: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ *</label>
                  <input type="text" value={formData.cnpj} onChange={(e) => setFormData(p => ({ ...p, cnpj: e.target.value }))} required className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email de Acesso *</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} required className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Senha {editingClient ? "(deixe vazio para manter)" : "*"}</label>
                  <input type="password" value={formData.password} onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))} required={!editingClient} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
              </div>

              <h4 className="font-medium text-gray-700 pt-2 border-t">Contato</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Contato</label>
                  <input type="text" value={formData.contatoNome} onChange={(e) => setFormData(p => ({ ...p, contatoNome: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input type="text" value={formData.contatoTelefone} onChange={(e) => setFormData(p => ({ ...p, contatoTelefone: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email do Contato</label>
                  <input type="email" value={formData.contatoEmail} onChange={(e) => setFormData(p => ({ ...p, contatoEmail: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
              </div>

              <h4 className="font-medium text-gray-700 pt-2 border-t">Endereço</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                  <input type="text" value={formData.cep} onChange={(e) => setFormData(p => ({ ...p, cep: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logradouro</label>
                  <input type="text" value={formData.logradouro} onChange={(e) => setFormData(p => ({ ...p, logradouro: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
                  <input type="text" value={formData.numero} onChange={(e) => setFormData(p => ({ ...p, numero: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                  <input type="text" value={formData.complemento} onChange={(e) => setFormData(p => ({ ...p, complemento: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
                  <input type="text" value={formData.bairro} onChange={(e) => setFormData(p => ({ ...p, bairro: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                  <input type="text" value={formData.cidade} onChange={(e) => setFormData(p => ({ ...p, cidade: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <input type="text" value={formData.estado} onChange={(e) => setFormData(p => ({ ...p, estado: e.target.value }))} maxLength={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button type="button" onClick={closeModal} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">Cancelar</button>
                <button type="submit" className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Unidade */}
      {showUnidadeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-2xl p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingUnidade ? "Editar Unidade" : "Nova Unidade"}</h3>
              <button onClick={closeUnidadeModal}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleUnidadeSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Unidade *</label>
                  <input type="text" value={unidadeForm.nome} onChange={(e) => setUnidadeForm(p => ({ ...p, nome: e.target.value }))} required className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ</label>
                  <input type="text" value={unidadeForm.cnpj} onChange={(e) => setUnidadeForm(p => ({ ...p, cnpj: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
              </div>

              <h4 className="font-medium text-gray-700 pt-2 border-t">Contato</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Contato</label>
                  <input type="text" value={unidadeForm.contatoNome} onChange={(e) => setUnidadeForm(p => ({ ...p, contatoNome: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input type="text" value={unidadeForm.contatoTelefone} onChange={(e) => setUnidadeForm(p => ({ ...p, contatoTelefone: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={unidadeForm.contatoEmail} onChange={(e) => setUnidadeForm(p => ({ ...p, contatoEmail: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
              </div>

              <h4 className="font-medium text-gray-700 pt-2 border-t">Endereço</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                  <input type="text" value={unidadeForm.cep} onChange={(e) => setUnidadeForm(p => ({ ...p, cep: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logradouro</label>
                  <input type="text" value={unidadeForm.logradouro} onChange={(e) => setUnidadeForm(p => ({ ...p, logradouro: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
                  <input type="text" value={unidadeForm.numero} onChange={(e) => setUnidadeForm(p => ({ ...p, numero: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                  <input type="text" value={unidadeForm.complemento} onChange={(e) => setUnidadeForm(p => ({ ...p, complemento: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
                  <input type="text" value={unidadeForm.bairro} onChange={(e) => setUnidadeForm(p => ({ ...p, bairro: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                  <input type="text" value={unidadeForm.cidade} onChange={(e) => setUnidadeForm(p => ({ ...p, cidade: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <input type="text" value={unidadeForm.estado} onChange={(e) => setUnidadeForm(p => ({ ...p, estado: e.target.value }))} maxLength={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button type="button" onClick={closeUnidadeModal} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">Cancelar</button>
                <button type="submit" className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Usuário do Cliente */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{editingUser ? "Editar Usuário" : "Novo Usuário"}</h3>
              <button onClick={closeUserModal}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleUserSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input type="text" value={userForm.name} onChange={(e) => setUserForm(p => ({ ...p, name: e.target.value }))} required 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" value={userForm.email} onChange={(e) => setUserForm(p => ({ ...p, email: e.target.value }))} required 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Senha {editingUser ? "(deixe vazio para manter)" : "*"}</label>
                <input type="password" value={userForm.password} onChange={(e) => setUserForm(p => ({ ...p, password: e.target.value }))} required={!editingUser} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Perfil</label>
                <select value={userForm.role} onChange={(e) => setUserForm(p => ({ ...p, role: e.target.value }))} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900">
                  {clientRoles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permissões de Visualização</label>
                <div className="space-y-2 p-3 bg-gray-50 rounded-lg border">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={userForm.permPmoc} onChange={(e) => setUserForm(p => ({ ...p, permPmoc: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                    <span className="text-sm text-gray-700">PMOC (Laudos, Relatórios, Formulários, ARTs)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={userForm.permNf} onChange={(e) => setUserForm(p => ({ ...p, permNf: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                    <span className="text-sm text-gray-700">Notas Fiscais</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={userForm.permProposta} onChange={(e) => setUserForm(p => ({ ...p, permProposta: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                    <span className="text-sm text-gray-700">Propostas Comerciais</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">Desmarque para restringir o acesso a determinados tipos de documento</p>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeUserModal} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">Cancelar</button>
                <button type="submit" className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
